// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — "Cite this view": what a view on the globe is, written so a
 * paper, a slide or a CSV can say where its numbers came from and how
 * to get back to them (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 1).
 *
 * Three forms of one citation — APA-like text, BibTeX, RIS — and the
 * same facts as CSV header rows ({@link provenanceRows}). Written in
 * English whatever the interface language, like the CSV headers: a
 * citation is pasted into the paper, not read in the app.
 *
 * The facts come from the dataset row and its `.dsa` (`dsaMetadata.ts`):
 * who made the data, the publisher's credit line, each source with its
 * licence. For CC BY sources the credit is a condition of the licence,
 * so it travels with every form. Pure: no DOM, no app state.
 */

import type { DsaAttribution } from '../dsaMetadata'
import { formatFrameIso, type FramePrecision } from '../frameCalendar'

/** Who runs AYNI XR, named in every citation as the visualising software's maker. */
export const AYNI_XR_PUBLISHER = 'Pachamama Studios B.V.'

/**
 * AYNI XR's own DOI: the Zenodo concept DOI, which always resolves to the
 * latest version (research plan, decision R4; record published by the
 * owner 2026-10-06, version 0.6.0 is 10.5281/zenodo.23181568). Every
 * citation and CSV carries it.
 */
export const AYNI_XR_DOI: string | null = '10.5281/zenodo.23181567'

declare const __APP_VERSION__: string

/** `AYNI XR <version>`, the version Vite stamps into the build; bare in tests. */
export function ayniXrSoftware(): string {
  const version = typeof __APP_VERSION__ === 'string' && __APP_VERSION__.length > 0 ? __APP_VERSION__ : null
  return version ? `AYNI XR ${version}` : 'AYNI XR'
}

export interface CitationInput {
  readonly datasetId: string
  /** The dataset's URL slug, for a readable citation key; else the title is used. */
  readonly slug?: string | null
  readonly title: string
  /** Who made the data (`.dsa` creator, else the catalogue's organisation); null when unknown. */
  readonly author: string | null
  /** When the data was published or last revised, ISO; null when unknown. */
  readonly published: string | null
  /** The publisher's credit line and sources, from the `.dsa`. */
  readonly credit: DsaAttribution | null
  /** The frame on screen, epoch ms; null for a dataset without dates. */
  readonly frameMs: number | null
  /**
   * AYNI: what the frame's time names (`frameCalendar.ts`): its year, month,
   * day or minute. A yearly frame is cited as "2075", not the instant of its
   * stamp; null or absent (a catalogue dataset): the minute, as before.
   */
  readonly framePrecision?: FramePrecision | null
  /** The link that reopens this view (`viewLink.ts`). */
  readonly viewUrl: string
  readonly accessed: Date
  /** `AYNI XR <version>`. */
  readonly software: string
  /** The software's DOI, bare (`10.5281/zenodo.…`); null or absent: none yet. */
  readonly softwareDoi?: string | null
  /**
   * AYNI: the release, its source, step, codec error and ± rule
   * (`valueFidelity.ts` `custodyRows`; value error audit, 2026-10-19). Written
   * into every export's header after the credit; absent for a dataset that
   * is not a value-encoded release.
   */
  readonly custody?: readonly (readonly [string, ...(string | number | null)[]])[]
}

/**
 * Who a citation names as the data's author: the catalogue's
 * organisation (for a stream, the index's `organization`, e.g. "IPCC
 * AR6 WGI Interactive Atlas"), else the `.dsa` creator. Neither counts
 * when it names a tool or a host rather than the data's makers: the
 * `.dsa` creator of our own streams is "AYNI dataset builder", and a
 * stream row without an organisation falls back to "Cloudflare R2"
 * (`dataService.ts`).
 */
export function citationAuthor(organization: string | null | undefined, creator: string | null | undefined): string | null {
  const usable = (name: string | null | undefined): string | null => {
    const s = name?.trim()
    return s && !/\bAYNI\b|dataset builder|^Cloudflare R2$/i.test(s) ? s : null
  }
  return usable(organization) ?? usable(creator)
}

export type ProvenanceCell = string | number | null
export type ProvenanceRow = readonly ProvenanceCell[]

/**
 * `2026-08-27 21:00 UTC`; at a coarser precision the frame's year, month or
 * day as ISO 8601 (`2075`, `2003-08`, `2026-08-27`), at a finer one with
 * its seconds.
 */
export function formatFrameTime(ms: number, precision?: FramePrecision | null): string {
  if (precision === 'year' || precision === 'month' || precision === 'day') return formatFrameIso(ms, precision)
  const iso = new Date(ms).toISOString()
  return `${iso.slice(0, precision === 'second' ? 19 : 16).replace('T', ' ')} UTC`
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function yearOf(iso: string | null): string | null {
  const year = iso ? /^(\d{4})/.exec(iso)?.[1] : null
  return year ?? null
}

/** Each distinct licence, by name (else its link), in source order. */
export function licencesOf(credit: DsaAttribution | null): string[] {
  const out: string[] = []
  for (const source of credit?.sources ?? []) {
    const name = source.licenseName ?? source.licenseUrl
    if (name && !out.includes(name)) out.push(name)
  }
  return out
}

/** AYNI XR and its maker, and its DOI once there is one. */
function softwareName(input: CitationInput): string {
  const doi = input.softwareDoi?.trim()
  return `${input.software} (${AYNI_XR_PUBLISHER}${doi ? `, https://doi.org/${doi}` : ''})`
}

/** The sentence after the title: the frame, the credit, the sources. */
function noteParts(input: CitationInput): string[] {
  const parts: string[] = []
  if (input.frameMs !== null) parts.push(`Frame ${formatFrameTime(input.frameMs, input.framePrecision)}`)
  const credit = input.credit?.text.trim()
  if (credit) parts.push(credit.replace(/\.$/, ''))
  const licences = licencesOf(input.credit)
  if (licences.length) parts.push(`Licence: ${licences.join('; ')}`)
  parts.push(`Visualised with ${softwareName(input)}`)
  return parts
}

/**
 * APA-like, for data: Author (year). *Title* [Data set; frame …]. Credit.
 * Visualised with AYNI XR. Link (accessed date). Without an author the
 * title leads, as APA does; without a date, "n.d.".
 */
export function citationText(input: CitationInput): string {
  const year = yearOf(input.published) ?? 'n.d.'
  const bracket = input.frameMs !== null ? `[Data set; frame ${formatFrameTime(input.frameMs, input.framePrecision)}]` : '[Data set]'
  const lead = input.author ? `${input.author} (${year}). ${input.title} ${bracket}.` : `${input.title} ${bracket}. (${year}).`
  const rest = noteParts(input).filter((p) => !p.startsWith('Frame '))
  return `${lead} ${rest.join('. ')}. ${input.viewUrl} (accessed ${isoDay(input.accessed)}).`
}

/** BibTeX's special characters, escaped for a field in braces. */
export function bibEscape(value: string): string {
  return value
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/([{}%&$#_])/g, '\\$1')
    .replace(/\^/g, '\\^{}')
    .replace(/~/g, '\\~{}')
}

/** A citation key: `ayni_<slug-ish>_<yyyymmdd>`, ASCII only. */
export function citationKey(input: CitationInput): string {
  const stem = (input.slug || input.title)
    .normalize('NFKD')
    // The accents NFKD split off, dropped, so "Météo" keys as "meteo".
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .slice(0, 40) || 'view'
  // A yearly or monthly frame keys by its year or month, as it is cited.
  const p = input.framePrecision
  const day = input.frameMs !== null && (p === 'year' || p === 'month')
    ? formatFrameIso(input.frameMs, p).replace(/-/g, '')
    : isoDay(input.frameMs !== null ? new Date(input.frameMs) : input.accessed).replace(/-/g, '')
  return `ayni_${stem}_${day}`
}

export function citationBibtex(input: CitationInput): string {
  const fields: Array<[string, string]> = []
  // Double braces keep an organisation's name whole instead of being
  // split into a surname and initials.
  if (input.author) fields.push(['author', `{${bibEscape(input.author)}}`])
  fields.push(['title', `{${bibEscape(input.title)}}`])
  const year = yearOf(input.published)
  if (year) fields.push(['year', year])
  fields.push(['howpublished', `\\url{${input.viewUrl.replace(/[{}\\]/g, '')}}`])
  fields.push(['note', bibEscape(`Data set. ${noteParts(input).join('. ')}.`)])
  fields.push(['urldate', isoDay(input.accessed)])
  const width = Math.max(...fields.map(([k]) => k.length))
  const body = fields.map(([k, v]) => `  ${k.padEnd(width)} = {${v}},`).join('\n')
  return `@misc{${citationKey(input)},\n${body}\n}\n`
}

/** RIS (Zotero, Mendeley, EndNote): one tag per line, CRLF, `ER` last. */
export function citationRis(input: CitationInput): string {
  const lines: Array<[string, string]> = [['TY', 'DATA'], ['TI', input.title]]
  if (input.author) lines.push(['AU', input.author])
  const year = yearOf(input.published)
  if (year) lines.push(['PY', year])
  lines.push(['UR', input.viewUrl])
  lines.push(['Y2', isoDay(input.accessed).replace(/-/g, '/')])
  lines.push(['DB', 'AYNI XR'])
  lines.push(['N1', `${noteParts(input).join('. ')}.`])
  for (const source of input.credit?.sources ?? []) {
    if (source.sourcePage) lines.push(['L2', source.sourcePage])
  }
  lines.push(['ER', ''])
  return lines.map(([tag, value]) => `${tag}  - ${value.replace(/[\r\n]+/g, ' ')}`.trimEnd()).join('\r\n') + '\r\n'
}

/**
 * The provenance block for a CSV header: what the numbers are of, where
 * they came from, under which licence, and the link back. Rows only;
 * the exporter places them after its own header rows.
 */
export function provenanceRows(input: CitationInput, opts: { exportedAt?: Date } = {}): ProvenanceRow[] {
  const rows: ProvenanceRow[] = [['dataset_id', input.datasetId]]
  if (input.frameMs !== null) {
    // At the frame's precision (a yearly frame is "2075"), as the pins' CSV writes its dates.
    const p = input.framePrecision
    rows.push(['frame_time', p ? formatFrameIso(input.frameMs, p) : new Date(input.frameMs).toISOString()])
  }
  rows.push(['view_url', input.viewUrl])
  if (input.author) rows.push(['data_author', input.author])
  const credit = input.credit?.text.trim()
  if (credit) rows.push(['credit', credit])
  for (const source of input.credit?.sources ?? []) {
    rows.push(['source', source.label, source.licenseName ?? source.licenseUrl, source.sourcePage])
  }
  for (const row of input.custody ?? []) rows.push([...row] as ProvenanceRow)
  rows.push(['software', softwareName(input)])
  if (opts.exportedAt) rows.push(['exported_at', opts.exportedAt.toISOString()])
  rows.push(['cite_as', citationText(input)])
  return rows
}
